import { useEffect, useRef, useCallback } from 'react';
import { interactionsAPI } from '../lib/api';

/**
 * useInteractionTracker - Track user interactions in Suite Ministerial.
 */
export function useInteractionTracker(entityType, entityId, entityTitle, churchId, options = {}) {
  const { trackPageView = true, trackTimeSpent = true } = options;
  const startTimeRef = useRef(Date.now());
  const hasSentViewRef = useRef(false);
  const sessionIdRef = useRef(`suite-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`);

  // Refs for values used in cleanup to avoid stale closures
  const entityTypeRef = useRef(entityType);
  const entityIdRef = useRef(entityId);
  const entityTitleRef = useRef(entityTitle);
  const churchIdRef = useRef(churchId);

  useEffect(() => { entityTypeRef.current = entityType; }, [entityType]);
  useEffect(() => { entityIdRef.current = entityId; }, [entityId]);
  useEffect(() => { entityTitleRef.current = entityTitle; }, [entityTitle]);
  useEffect(() => { churchIdRef.current = churchId; }, [churchId]);

  // Send a view event on mount (once per entity)
  useEffect(() => {
    if (!trackPageView || !entityType || !entityId || hasSentViewRef.current) return;

    hasSentViewRef.current = true;
    startTimeRef.current = Date.now();

    interactionsAPI.track({
      entity_type: entityType,
      entity_id: entityId,
      entity_title: entityTitle || undefined,
      interaction_type: 'view',
      church_id: churchId || undefined,
      session_id: sessionIdRef.current,
      metadata: { source: 'suite_ministerial', screen: window.location.pathname }
    }).catch(() => {});
  }, [entityType, entityId, entityTitle, churchId, trackPageView]);

  // Send time_spent on unmount
  useEffect(() => {
    if (!trackTimeSpent || !entityType || !entityId) return;
    startTimeRef.current = Date.now();

    return () => {
      const elapsed = Date.now() - startTimeRef.current;
      if (elapsed > 3000) {
        interactionsAPI.track({
          entity_type: entityTypeRef.current,
          entity_id: entityIdRef.current,
          entity_title: entityTitleRef.current || undefined,
          interaction_type: 'time_spent',
          duration_ms: Math.min(elapsed, 1800000),
          church_id: churchIdRef.current || undefined,
          session_id: sessionIdRef.current,
          metadata: { source: 'suite_ministerial', screen: window.location.pathname }
        }).catch(() => {});
      }
    };
  }, [entityType, entityId, trackTimeSpent]);

  const trackClick = useCallback((itemId, itemTitle) => {
    if (!entityTypeRef.current) return;
    interactionsAPI.track({
      entity_type: entityTypeRef.current,
      entity_id: itemId,
      entity_title: itemTitle || undefined,
      interaction_type: 'click',
      church_id: churchIdRef.current || undefined,
      session_id: sessionIdRef.current,
      metadata: { source: 'suite_ministerial', screen: window.location.pathname }
    }).catch(() => {});
  }, []);

  const trackView = useCallback((itemId, itemTitle) => {
    if (!entityTypeRef.current) return;
    interactionsAPI.track({
      entity_type: entityTypeRef.current,
      entity_id: itemId,
      entity_title: itemTitle || undefined,
      interaction_type: 'view',
      church_id: churchIdRef.current || undefined,
      session_id: sessionIdRef.current,
      metadata: { source: 'suite_ministerial', screen: window.location.pathname }
    }).catch(() => {});
  }, []);

  const trackBatchViews = useCallback((items) => {
    if (!entityTypeRef.current || !items || items.length === 0) return;
    const events = items.map(item => ({
      entity_type: entityTypeRef.current,
      entity_id: item.id,
      entity_title: item.title || item.attributes?.title || undefined,
      interaction_type: 'view',
      church_id: churchIdRef.current || undefined,
      session_id: sessionIdRef.current,
      metadata: { source: 'suite_ministerial', screen: window.location.pathname }
    }));
    interactionsAPI.trackBatch(events).catch(() => {});
  }, []);

  return { trackClick, trackView, trackBatchViews };
}

/**
 * useItemTimeTracker - Track time spent viewing a dialog/item.
 */
export function useItemTimeTracker(entityType, entityId, entityTitle, churchId, isOpen) {
  const startTimeRef = useRef(null);
  const sessionIdRef = useRef(`suite-dialog-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`);
  const prevOpenRef = useRef(false);

  useEffect(() => {
    if (isOpen && entityId && !prevOpenRef.current) {
      startTimeRef.current = Date.now();
    } else if (!isOpen && prevOpenRef.current && startTimeRef.current && entityId) {
      const elapsed = Date.now() - startTimeRef.current;
      if (elapsed > 2000) {
        interactionsAPI.track({
          entity_type: entityType,
          entity_id: entityId,
          entity_title: entityTitle || undefined,
          interaction_type: 'time_spent',
          duration_ms: Math.min(elapsed, 1800000),
          church_id: churchId || undefined,
          session_id: sessionIdRef.current,
          metadata: { source: 'suite_ministerial', context: 'dialog' }
        }).catch(() => {});
      }
      startTimeRef.current = null;
    }
    prevOpenRef.current = isOpen;
  }, [isOpen, entityType, entityId, entityTitle, churchId]);
}
